import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { ExternalLink, TrendingUp, Sparkles, ChevronRight, X, Zap, BarChart2, Send } from 'lucide-react';
import { 
  BANNER_CONFIGS, 
  BANNER_BRANDS, 
  BANNER_SLOTS, 
  ROTATION_CONFIG,
  getPageType,
  getPrioritizedBanners 
} from './BannerData';
import { trackImpression, trackClick } from './BannerTracking';
import { buildRedirectUrl, handleCtaClick } from '../../utils/fbTracking';

// Build tracked URL for funbet links, pass others through
const getTrackedUrl = (url) => {
  if (url && url.includes('funbet.me')) return buildRedirectUrl(url);
  return url;
};

// Telegram icon component
const TelegramIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
  </svg>
);

// Banner rotation state manager
let globalRotationIndex = Math.floor(Math.random() * Object.keys(BANNER_CONFIGS).length);
const slotIndices = {};

const getNextBannerForSlot = (slotId, pageType) => {
  const banners = getPrioritizedBanners(pageType);
  if (slotIndices[slotId] === undefined) {
    const slotOffset = Object.keys(BANNER_SLOTS).indexOf(
      Object.keys(BANNER_SLOTS).find(k => BANNER_SLOTS[k] === slotId)
    );
    slotIndices[slotId] = (globalRotationIndex + slotOffset) % banners.length;
  }
  return banners[slotIndices[slotId]];
};

const rotateBannerForSlot = (slotId, pageType) => {
  const banners = getPrioritizedBanners(pageType);
  slotIndices[slotId] = ((slotIndices[slotId] || 0) + 1) % banners.length;
  return banners[slotIndices[slotId]];
};

// Premium Top Banner - Sleek horizontal strip
export const TopBanner = ({ className = '' }) => {
  const location = useLocation();
  const pageType = getPageType(location.pathname);
  const [currentBanner, setCurrentBanner] = useState(() => 
    getNextBannerForSlot(BANNER_SLOTS.TOP, pageType)
  );
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    trackImpression(currentBanner.id, BANNER_SLOTS.TOP, location.pathname);
  }, [currentBanner?.id, location.pathname]);

  useEffect(() => {
    if (!ROTATION_CONFIG.enabled) return;
    const interval = setInterval(() => {
      setCurrentBanner(rotateBannerForSlot(BANNER_SLOTS.TOP, pageType));
    }, ROTATION_CONFIG.intervalMs);
    return () => clearInterval(interval);
  }, [pageType]);

  const handleClick = () => {
    trackClick(currentBanner.id, BANNER_SLOTS.TOP, location.pathname, currentBanner.url);
  };

  const { headline, subheadline, cta, url, colors, brand, icon } = currentBanner;
  const isFunBetMe = brand === 'FunBet.Me';
  const isTelegram = brand === 'Telegram';

  return (
    <div className={`w-full ${className}`}>
      <a
        href={getTrackedUrl(url)}
        target="_blank"
        rel="noopener noreferrer"
        onClick={handleClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className="block"
        data-testid="top-banner"
      >
        <div 
          className="relative overflow-hidden transition-all duration-500"
          style={{
            background: isTelegram
              ? 'linear-gradient(90deg, #0a0a0a 0%, #0a1520 50%, #0a0a0a 100%)'
              : isFunBetMe 
                ? 'linear-gradient(90deg, #0a0a0a 0%, #0f1a0f 50%, #0a0a0a 100%)'
                : 'linear-gradient(90deg, #0a0a0a 0%, #0a0f1a 50%, #0a0a0a 100%)',
            borderBottom: `1px solid ${colors.primary}15`
          }}
        >
          {/* Animated gradient line */}
          <div 
            className="absolute bottom-0 left-0 right-0 h-[1px] transition-opacity duration-300"
            style={{
              background: `linear-gradient(90deg, transparent, ${colors.primary}60, transparent)`,
              opacity: isHovered ? 1 : 0.5
            }}
          />
          
          <div className="max-w-7xl mx-auto px-4 py-2.5 flex items-center justify-center gap-4 sm:gap-8">
            {/* Brand indicator */}
            <div className="hidden sm:flex items-center gap-2">
              {isTelegram ? (
                <TelegramIcon className="w-4 h-4" style={{ color: colors.primary }} />
              ) : (
                <div 
                  className="w-1.5 h-1.5 rounded-full animate-pulse"
                  style={{ background: colors.primary }}
                />
              )}
              <span className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-medium">
                {isTelegram ? 'Join Us' : 'Featured'}
              </span>
            </div>
            
            {/* Main content */}
            <div className="flex items-center gap-3 sm:gap-6">
              {isTelegram && (
                <TelegramIcon className="w-5 h-5 sm:hidden" style={{ color: colors.primary }} />
              )}
              <span 
                className="text-sm sm:text-base font-semibold tracking-tight"
                style={{ color: colors.accent }}
              >
                {headline}
              </span>
              <span className="hidden md:inline text-slate-600">|</span>
              <span className="hidden md:inline text-xs sm:text-sm text-slate-400">
                {subheadline}
              </span>
            </div>
            
            {/* CTA */}
            <div 
              className="flex items-center gap-1.5 text-xs sm:text-sm font-semibold transition-all duration-300"
              style={{ 
                color: colors.primary,
                transform: isHovered ? 'translateX(2px)' : 'translateX(0)'
              }}
            >
              {cta}
              <ChevronRight className="w-3.5 h-3.5" />
            </div>
          </div>
        </div>
      </a>
    </div>
  );
};

// Premium Mid-Page Banner - Card style with glass effect
export const MidPageBanner = ({ className = '', horizontal = true }) => {
  const location = useLocation();
  const pageType = getPageType(location.pathname);
  const [currentBanner, setCurrentBanner] = useState(() => 
    getNextBannerForSlot(BANNER_SLOTS.MID_PAGE, pageType)
  );
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    trackImpression(currentBanner.id, BANNER_SLOTS.MID_PAGE, location.pathname);
  }, [currentBanner?.id, location.pathname]);

  useEffect(() => {
    if (!ROTATION_CONFIG.enabled) return;
    const interval = setInterval(() => {
      setCurrentBanner(rotateBannerForSlot(BANNER_SLOTS.MID_PAGE, pageType));
    }, ROTATION_CONFIG.intervalMs);
    return () => clearInterval(interval);
  }, [pageType]);

  const handleClick = () => {
    trackClick(currentBanner.id, BANNER_SLOTS.MID_PAGE, location.pathname, currentBanner.url);
  };

  const { headline, subheadline, cta, url, colors, brand, icon } = currentBanner;
  const isFunBetMe = brand === 'FunBet.Me';
  const isTelegram = brand === 'Telegram';
  const Icon = isTelegram ? TelegramIcon : (isFunBetMe ? TrendingUp : BarChart2);

  return (
    <div className={`my-6 ${className}`}>
      <a
        href={getTrackedUrl(url)}
        target="_blank"
        rel="noopener noreferrer"
        onClick={handleClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className="block group"
        data-testid="banner-mid_page_banner"
      >
        <div 
          className="relative overflow-hidden rounded-lg transition-all duration-500"
          style={{
            background: isTelegram 
              ? 'linear-gradient(135deg, #0a1520 0%, #0d0f12 100%)'
              : 'linear-gradient(135deg, #111318 0%, #0d0f12 100%)',
            border: `1px solid ${isHovered ? colors.primary + '30' : '#1e293b30'}`,
            boxShadow: isHovered ? `0 0 40px ${colors.primary}10` : 'none'
          }}
        >
          {/* Subtle corner accent */}
          <div 
            className="absolute top-0 right-0 w-32 h-32 opacity-20"
            style={{
              background: `radial-gradient(circle at top right, ${colors.primary}30, transparent 70%)`
            }}
          />
          
          <div className="relative p-5 sm:p-6 flex items-center gap-5">
            {/* Icon */}
            <div 
              className="hidden sm:flex items-center justify-center w-12 h-12 rounded-lg transition-transform duration-300"
              style={{ 
                background: `${colors.primary}10`,
                border: `1px solid ${colors.primary}20`,
                transform: isHovered ? 'scale(1.05)' : 'scale(1)'
              }}
            >
              <Icon 
                className="w-5 h-5 transition-colors duration-300" 
                style={{ color: colors.primary }}
              />
            </div>
            
            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h3 
                  className="text-base sm:text-lg font-semibold text-white tracking-tight"
                >
                  {headline}
                </h3>
                <span 
                  className="hidden sm:inline px-2 py-0.5 text-[9px] uppercase tracking-wider rounded font-medium"
                  style={{ 
                    background: `${colors.primary}15`,
                    color: colors.primary
                  }}
                >
                  {isTelegram ? 'Free' : 'Partner'}
                </span>
              </div>
              <p className="text-sm text-slate-400 leading-relaxed">
                {subheadline}
              </p>
            </div>
            
            {/* CTA Button */}
            <div 
              className="flex-shrink-0 flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all duration-300"
              style={{ 
                background: isHovered ? colors.primary : `${colors.primary}15`,
                color: isHovered ? '#0a0a0a' : colors.primary,
                border: `1px solid ${colors.primary}30`
              }}
            >
              {cta}
              <ExternalLink className="w-3.5 h-3.5" />
            </div>
          </div>
        </div>
      </a>
    </div>
  );
};

// Premium Mobile Sticky Footer
export const StickyFooterBanner = ({ className = '' }) => {
  const location = useLocation();
  const pageType = getPageType(location.pathname);
  const [currentBanner, setCurrentBanner] = useState(() => 
    getNextBannerForSlot(BANNER_SLOTS.STICKY_FOOTER, pageType)
  );
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    if (currentBanner && !isDismissed) {
      trackImpression(currentBanner.id, BANNER_SLOTS.STICKY_FOOTER, location.pathname);
    }
  }, [currentBanner, isDismissed, location.pathname]);

  useEffect(() => {
    if (!ROTATION_CONFIG.enabled) return;
    const interval = setInterval(() => {
      setCurrentBanner(rotateBannerForSlot(BANNER_SLOTS.STICKY_FOOTER, pageType));
    }, ROTATION_CONFIG.intervalMs);
    return () => clearInterval(interval);
  }, [pageType]);

  const handleClick = () => {
    trackClick(currentBanner.id, BANNER_SLOTS.STICKY_FOOTER, location.pathname, currentBanner.url);
  };

  const handleDismiss = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDismissed(true);
  };

  if (isDismissed) return null;

  const { headline, cta, url, colors, brand } = currentBanner;
  const isFunBetMe = brand === 'FunBet.Me';
  const isTelegram = brand === 'Telegram';

  return (
    <div 
      className={`fixed bottom-0 left-0 right-0 z-50 sm:hidden ${className}`}
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      data-testid="sticky-footer-banner"
    >
      <div 
        className="relative"
        style={{
          background: 'linear-gradient(180deg, #0a0a0a00 0%, #0a0a0a 20%)',
          paddingTop: '20px'
        }}
      >
        <a
          href={getTrackedUrl(url)}
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleClick}
          className="block mx-3 mb-3"
        >
          <div 
            className="relative overflow-hidden rounded-xl p-3.5 backdrop-blur-xl"
            style={{
              background: 'linear-gradient(135deg, #15171c 0%, #0f1115 100%)',
              border: `1px solid ${colors.primary}20`,
              boxShadow: `0 -4px 30px rgba(0,0,0,0.5), 0 0 20px ${colors.primary}10`
            }}
          >
            {/* Dismiss button */}
            <button
              onClick={handleDismiss}
              className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center rounded-full bg-slate-800/80 text-slate-500 hover:text-white transition-colors"
            >
              <X className="w-3.5 h-3.5" />
            </button>
            
            <div className="flex items-center gap-3 pr-6">
              {/* Indicator */}
              <div 
                className="w-10 h-10 rounded-lg flex items-center justify-center"
                style={{ background: `${colors.primary}15` }}
              >
                {isTelegram ? (
                  <TelegramIcon className="w-5 h-5" style={{ color: colors.primary }} />
                ) : isFunBetMe ? (
                  <TrendingUp className="w-5 h-5" style={{ color: colors.primary }} />
                ) : (
                  <BarChart2 className="w-5 h-5" style={{ color: colors.primary }} />
                )}
              </div>
              
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-white truncate">{headline}</p>
                <p className="text-[11px] text-slate-500">Tap to learn more</p>
              </div>
              
              <div 
                className="px-4 py-2 rounded-lg text-xs font-bold"
                style={{ 
                  background: colors.primary,
                  color: '#0a0a0a'
                }}
              >
                {cta}
              </div>
            </div>
          </div>
        </a>
      </div>
    </div>
  );
};

// Premium Inline Banner - Minimal and elegant
export const InlineBanner = ({ className = '', minimal = false }) => {
  const location = useLocation();
  const pageType = getPageType(location.pathname);
  const [currentBanner, setCurrentBanner] = useState(() => 
    getNextBannerForSlot(BANNER_SLOTS.INLINE, pageType)
  );
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    trackImpression(currentBanner.id, BANNER_SLOTS.INLINE, location.pathname);
  }, [currentBanner?.id, location.pathname]);

  const handleClick = () => {
    trackClick(currentBanner.id, BANNER_SLOTS.INLINE, location.pathname, currentBanner.url);
  };

  const { headline, cta, url, colors, brand } = currentBanner;
  const isFunBetMe = brand === 'FunBet.Me';
  const isTelegram = brand === 'Telegram';
  const Icon = isTelegram ? TelegramIcon : (isFunBetMe ? TrendingUp : Sparkles);

  if (minimal) {
    return (
      <a
        href={getTrackedUrl(url)}
        target="_blank"
        rel="noopener noreferrer"
        onClick={handleClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className={`
          inline-flex items-center gap-2 px-3 py-1.5 
          rounded-full text-xs font-medium
          transition-all duration-300
          ${className}
        `}
        style={{ 
          background: isHovered ? `${colors.primary}20` : `${colors.primary}10`,
          color: colors.primary,
          border: `1px solid ${colors.primary}20`
        }}
        data-testid="inline-banner"
      >
        <Icon className="w-3 h-3" />
        <span>{brand}</span>
        <ChevronRight className="w-3 h-3" />
      </a>
    );
  }

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={`block group ${className}`}
      data-testid="inline-banner"
    >
      <div 
        className="relative overflow-hidden rounded-lg p-4 transition-all duration-300"
        style={{ 
          background: 'linear-gradient(135deg, #111318 0%, #0d0f12 100%)',
          border: `1px solid ${isHovered ? colors.primary + '30' : '#1e293b20'}`,
        }}
      >
        <div className="flex items-center gap-4">
          <div 
            className="w-9 h-9 rounded-lg flex items-center justify-center transition-transform duration-300"
            style={{ 
              background: `${colors.primary}10`,
              transform: isHovered ? 'scale(1.1)' : 'scale(1)'
            }}
          >
            <Icon className="w-4 h-4" style={{ color: colors.primary }} />
          </div>
          
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-white truncate">{headline}</p>
          </div>
          
          <div 
            className="flex items-center gap-1 text-xs font-semibold transition-all duration-300"
            style={{ 
              color: colors.primary,
              transform: isHovered ? 'translateX(2px)' : 'translateX(0)'
            }}
          >
            {cta}
            <ChevronRight className="w-3.5 h-3.5" />
          </div>
        </div>
      </div>
    </a>
  );
};

// Main reusable component (delegates to specific variants)
export const InternalPromoBanner = ({ 
  slot = BANNER_SLOTS.MID_PAGE,
  variant = 'default',
  className = '',
}) => {
  switch (slot) {
    case BANNER_SLOTS.TOP:
      return <TopBanner className={className} />;
    case BANNER_SLOTS.STICKY_FOOTER:
      return <StickyFooterBanner className={className} />;
    case BANNER_SLOTS.INLINE:
      return <InlineBanner className={className} minimal={variant === 'minimal'} />;
    default:
      return <MidPageBanner className={className} />;
  }
};

export default InternalPromoBanner;
